import * as React from 'react'

import {
  AGGREGATION_STANDARD,
  ENTITY_LABEL,
  ORDERED_SMART_GROUPS,
  ResultRow,
  SHOW_MORE_THRESHOLDS,
  SkillTag,
  getRowMetrics,
  getSortableEntityName,
} from './helpers'

import { CardMetrics, SmartGroupSection, UnpracticedBanner, bucketRowsBySmartGroup, cardFooterLine, cardMetricPrefix, } from '../../adminMomentumReports/smartGroups'
import { ADMIN_SKILLS_REPORT_PATH, NoPracticeDataEmptyState, } from '../../shared'
import { Tooltip, pluralize, } from '../../../Shared/index'
import { formatGradeLevelWithContext } from '../../../Teacher/components/progress_reports/standards_report/shared'

const renderStandardSkillTag = (skill: SkillTag, index: number) => {
  const { id, name, skill_sub_categories, } = skill
  const hint = 'Click this tag to view the Admin Skills Report for this Skill Category'
  const alignedTo = skill_sub_categories.length > 0
    ? `the following Skill Sub-Categories within the <strong>${name}</strong> Skill Category:<br/><br/><strong>${skill_sub_categories.join(', ')}</strong>`
    : `the <strong>${name}</strong> Skill Category.`
  const tooltipText = `This standard is aligned to ${alignedTo}<hr/>💡 <strong>Hint:</strong> ${hint}`

  return (
    <Tooltip
      key={id}
      tooltipText={tooltipText}
      tooltipTriggerText={(
        <a
          className={`quill-button extra-small green contained focus-on-light skill-tag${index === 0 ? ' first-skill-tag' : ''}`}
          href={`${ADMIN_SKILLS_REPORT_PATH}?skill_category_id=${id}`}
          rel="noopener noreferrer"
          target="_blank"
        >
          {name}
        </a>
      )}
    />
  )
}

interface StandardCardProps {
  row: ResultRow;
  smartGroup: string;
  goal: number;
  isSpecificStudent: boolean;
}

const StandardCard: React.FC<StandardCardProps> = ({ row, smartGroup, goal, isSpecificStudent, }) => {
  const { code, description, skills, grades, } = row
  const { momentumScore, exposure, questionsAnswered, } = getRowMetrics(row)

  const metricPrefix = cardMetricPrefix(isSpecificStudent)
  const footerLine = cardFooterLine(isSpecificStudent, row)

  return (
    <section className="smart-group-card standard-card">
      <div className="name-and-last-practiced">
        <div className="standard-data">
          <div className="standard-header">
            <h3>{code}</h3>
            <div className="grades">
              <span className="sr-only">Grades: </span>
              {(grades || []).map((grade) => {
                const gradeLevel = formatGradeLevelWithContext(grade)
                return (
                  <Tooltip
                    key={grade}
                    tooltipText={`This standard is part of the ${gradeLevel} curriculum.`}
                    tooltipTriggerText={<span className="grade-tag">{gradeLevel}</span>}
                  />
                )
              })}            </div>
          </div>
          <div className="skill-tags">
            <span className="sr-only">Skill Categories: </span>
            {(skills || []).map((skill, index) => renderStandardSkillTag(skill, index))}
          </div>
          <p className="standard-description">{description}</p>
        </div>
        <p className="standard-practice-info">{footerLine}</p>
      </div>
      <CardMetrics
        exposure={exposure}
        goal={goal}
        metricPrefix={metricPrefix}
        momentumScore={momentumScore}
        questionsAnswered={questionsAnswered}
        smartGroup={smartGroup}
      />
    </section>
  )
}

interface SmartGroupsViewProps {
  rows: ResultRow[];
  aggregation: string;
  goal: number;
  isSpecificStudent: boolean;
  onViewPerformanceBreakdown: () => void;
}

const SmartGroupsView: React.FC<SmartGroupsViewProps> = ({ rows, aggregation, goal, isSpecificStudent, onViewPerformanceBreakdown, }) => {
  const { grouped, unpracticed, practicedCount, } = React.useMemo(() => bucketRowsBySmartGroup(rows, goal), [rows, goal])

  // Unpracticed rows are always client-built placeholders (query rows have
  // practice by definition), so no non-standard grade names can appear here.
  const footerCountUnpracticed = unpracticed.length

  if (practicedCount === 0) return <NoPracticeDataEmptyState />

  const isStandard = aggregation === AGGREGATION_STANDARD

  const renderStandardCard = (row: ResultRow, smartGroup: string) => (
    <StandardCard
      goal={goal}
      isSpecificStudent={isSpecificStudent}
      row={row}
      smartGroup={smartGroup}
    />
  )

  const sections = ORDERED_SMART_GROUPS.map((smartGroup) => {
    if (!grouped[smartGroup]?.length) return null
    return (
      <SmartGroupSection
        aggregation={aggregation}
        entityLabel={ENTITY_LABEL[aggregation]}
        getSortableEntityName={getSortableEntityName}
        goal={goal}
        isSpecificStudent={isSpecificStudent}
        key={smartGroup}
        renderCard={isStandard ? renderStandardCard : undefined}
        rows={grouped[smartGroup]}
        smartGroup={smartGroup}
        visibleCount={SHOW_MORE_THRESHOLDS[aggregation]}
      />
    )
  }).filter(Boolean)

  const labels = ENTITY_LABEL[aggregation]

  // By Standard has no expected-standard count, so the banner copy is generic.
  function renderBanner() {
    if (isStandard) {
      return (
        <UnpracticedBanner
          buttonText="View all standards in Performance Breakdown"
          heading="Looking for unpracticed standards?"
          onClick={onViewPerformanceBreakdown}
        />
      )
    }

    if (footerCountUnpracticed === 0) return null

    return (
      <UnpracticedBanner
        buttonText={`View all ${labels.plural} in Performance Breakdown`}
        heading={`${footerCountUnpracticed} ${pluralize(footerCountUnpracticed, labels.singular, labels.plural)} ${pluralize(footerCountUnpracticed, "hasn't", "haven't")} practiced this standard yet`}
        onClick={onViewPerformanceBreakdown}
      />
    )
  }

  return (
    <div className="admin-skills-smart-groups admin-standards-smart-groups">
      {sections}
      {renderBanner()}
    </div>
  )
}

export default SmartGroupsView
